import React from "react";

import { useFeatureIsOn } from "@growthbook/growthbook-react";
import * as Sentry from "@sentry/browser";
import moment from "moment";
import { EditorState } from "prosemirror-state";
import { RRule, RRuleSet, rrulestr } from "rrule";
import { Popup } from "semantic-ui-react";

import { decodeGlobalId } from "~/lib/relayUtil";
import store from "~/lib/store";
import { isTeams } from "~/lib/utils";

import { weekDays } from "../calendar/hooks/useRecurrenceOptions";
import type { URLParams } from "./StreamHeader/StreamHeader";
import type { Note } from "./types";

import type {
	CustomRecurringRuleSerializerInput,
	Choices as WeekDays,
} from "~/__generated__/useSaveEventMutationMutation.graphql";

type GuestBlockedPopupProps = {
	trigger: React.ReactNode;
};

export function GuestBlockedPopup(props: GuestBlockedPopupProps) {
	return (
		<Popup
			inverted
			size="tiny"
			content="Guest users cannot perform this action"
			position="bottom center"
			trigger={<div>{props.trigger}</div>}
		/>
	);
}

export function parseRecurrenceRule(recurrenceRule: readonly (string | null)[]): RRule | RRuleSet | null {
	if (!recurrenceRule[0]) {
		return null;
	}
	const ruleString = recurrenceRule.find(r => r?.includes("RRULE"));
	let parseTarget = ruleString || recurrenceRule[0];
	parseTarget = parseTarget.replace(/;X-BUSYMAC-[^=]*=[^:;]*/g, "");
	try {
		return rrulestr(parseTarget);
	} catch (error) {
		Sentry.captureException(error);
		return null;
	}
}

export function recurrenceRuleToString(recurrenceRule: ReadonlyArray<string | null> | null | undefined): string {
	if (!recurrenceRule?.[0]) return "";

	const rrule = parseRecurrenceRule(recurrenceRule);
	if (!rrule) {
		return " ⚠️ We’re having trouble understanding this event, please contact support";
	}
	return rrule.toText();
}

export const streamStore = store.namespace("stream");

export function getStreamLastOpenedKey(id = "") {
	return `lastOpened.${id}`;
}

export function isCurrentYear(date: moment.Moment) {
	return moment().year() === date.year();
}

export function formatNoteDate(
	note: Pick<Note, "createdAt" | "eventStart" | "eventEnd" | "noteType">,
	isAllDay = false,
) {
	const { createdAt, eventStart, eventEnd, noteType } = note;

	let content = "";

	if (eventStart) {
		const noteStartTime = moment(eventStart);

		if (noteStartTime.isValid()) {
			content = noteStartTime.calendar(undefined, {
				sameDay: () => {
					const noteEndTime = moment(eventEnd);
					let prefix = "Today";
					const start = noteStartTime.format("h:mm A");
					const end = noteEndTime.format("h:mm A");

					if (isAllDay) {
						return `[${prefix}]`;
					} else {
						return `[${prefix}, ${start} - ${end}]`;
					}
				},
				lastWeek: isAllDay ? "MMM D" : "MMM D [at] h:mm A",
				sameElse: () => {
					const suffix = isAllDay ? "" : " [at] h:mm A";
					return isCurrentYear(noteStartTime) ? `MMM D${suffix}` : `MMM D, YYYY${suffix}`;
				},
			});
		}
	} else if (noteType === "goalset") {
		const noteCreatedAt = moment(createdAt || "");
		const format = isCurrentYear(noteCreatedAt) ? "MMM D" : "MMM D, YYYY";
		content = noteCreatedAt.format(format);
	} else if (noteType !== "oneonone") {
		const noteCreatedAt = moment(createdAt || "");
		let format;
		if (isCurrentYear(noteCreatedAt)) {
			if (isAllDay) {
				format = "MMM D";
			} else {
				format = "MMM D, h:mm A";
			}
		} else if (isAllDay) {
			format = "MMM D, YYYY";
		} else {
			format = "MMM D, YYYY h:mm A";
		}

		content = noteCreatedAt.format(format);
	}

	return content;
}

export function getLocalTabId(tabId: string) {
	const tabIdParts = decodeGlobalId(tabId)[1].split(":");
	return tabIdParts[tabIdParts.length - 1];
}

export function getRRuleFromRepeatAndInterval(
	repeat: string,
	start: Date,
	customRrule?: CustomRecurringRuleSerializerInput,
): RRule | null {
	let rrule: RRule;

	const freqMap = {
		DAILY: RRule.DAILY,
		WEEKLY: RRule.WEEKLY,
		MONTHLY: RRule.MONTHLY,
		YEARLY: RRule.YEARLY,
	};

	switch (repeat) {
		case "NEVER":
			return null;

		case "DAILY":
			rrule = new RRule({
				freq: RRule.DAILY,
				dtstart: start,
			});
			break;

		case "EVERYWEEKDAY":
			rrule = new RRule({
				freq: RRule.WEEKLY,
				byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
				dtstart: start,
			});
			break;

		case "WEEKLY":
			rrule = new RRule({
				freq: RRule.WEEKLY,
				byweekday: [weekDays[moment(start).isoWeekday() % 7]],
				dtstart: start,
			});
			break;

		case "BIWEEKLY":
			rrule = new RRule({
				freq: RRule.WEEKLY,
				interval: 2,
				byweekday: [weekDays[moment(start).isoWeekday() % 7]],
				dtstart: start,
			});
			break;

		case "MONTHLY":
			rrule = new RRule({
				freq: RRule.MONTHLY,
				dtstart: start,
			});
			break;

		case "YEARLY":
			rrule = new RRule({
				freq: RRule.YEARLY,
				dtstart: start,
			});
			break;

		case "CUSTOM": {
			if (!customRrule) return null;

			rrule = new RRule({
				freq: freqMap[customRrule.frequency],
				interval: customRrule.interval,
				byweekday: customRrule.weekDays as WeekDays[],
				dtstart: start,
				until: customRrule.untilDate ? moment(customRrule.untilDate).toDate() : null,
				count: customRrule.untilCount || null,
			});

			break;
		}

		default:
			return null;
	}

	return rrule;
}

export function getOccurrencesListFromRepeatAndInterval(
	repeat: string,
	start: Date,
	intervalFrom: Date,
	intervalTo: Date,
	customRrule?: CustomRecurringRuleSerializerInput,
): string[] {
	const rrule = getRRuleFromRepeatAndInterval(repeat, start, customRrule);

	if (!rrule) return [];

	return rrule.between(intervalFrom, intervalTo).map(date => date.toISOString());
}

export function noteIsEmpty(state: EditorState) {
	const doc = state.doc;
	return (
		doc.childCount === 1 && doc.firstChild?.type === state.schema.nodes["unstyled"] && doc.firstChild.textContent === ""
	);
}

export function noteContainsStreamTemplateOnly(state: EditorState, stream: Note["stream"]) {
	const templateBlocks = stream.template?.upgradedProseDoc.content;
	if (!templateBlocks || templateBlocks.length != state.doc.content.childCount) return false;
	let stateIsDefaultTemplate = true;
	state.doc.content.forEach((node, _, index) => {
		if (!stateIsDefaultTemplate) return;
		const templateBlock = templateBlocks[index];
		if (templateBlock.content && node.childCount) {
			node.forEach((child, _, index) => {
				if (!stateIsDefaultTemplate) return;
				const templateBlockChild = templateBlock.content[index];
				const equivalentChildren =
					(child.type.name === templateBlockChild.type && !Boolean(child.text)) ||
					child.text == templateBlockChild.text;
				if (!equivalentChildren) {
					stateIsDefaultTemplate = equivalentChildren;
				}
			});
		}
		const equivalent = node.type.name === templateBlock.type;
		if (!equivalent) {
			stateIsDefaultTemplate = equivalent;
		}
	});
	return stateIsDefaultTemplate;
}

/**
 * Given the URL params for the stream page, return the base URL of the stream, i.e. no tab selected.
 */
export function baseStreamUrl(urlParams: URLParams, isGuest: boolean) {
	const { id, event, user1, user2 } = urlParams;

	if (isTeams) {
		if (event) return `/apps/teams/tab/meetings/${event}/`;
		if (user1 && user2) return `/apps/teams/tab/1on1s/${user1}/${user2}/`;
		if (id) return `/apps/teams/tab/stream/${id}/`;
		return "/apps/teams/tab/stream/";
	}

	if (event) return `/meetings/${event}/`;

	if (user1 && user2) {
		if (isGuest) return `/guest/1on1s/${user1}/${user2}/`;
		return `/1on1s/${user1}/${user2}/`;
	}

	if (id) {
		if (isGuest) return `/guest/stream/${id}/`;
		return `/stream/${id}/`;
	}

	return "/";
}

export function formatLastUpdatedDate(lastUpdatedDate: moment.Moment): string {
	return lastUpdatedDate.year() === moment().year()
		? lastUpdatedDate.format("MMM D")
		: lastUpdatedDate.format("MMM D YYYY");
}

const RECORDING_ERROR_INDICATOR_FEATURE_FLAG = "notetaker-error-indicators";

export function useRecordingErrorIndicatorFeatureFlagOn(): boolean {
	return useFeatureIsOn(RECORDING_ERROR_INDICATOR_FEATURE_FLAG);
}
